import { z } from 'zod';
import type { PromotionChannelSchema } from './promotion';

/** Пакеты продвижения при создании события (issue #175). */
export const EventCreatePromotionPackageIdSchema = z.enum([
  'SOCIAL_NETWORKS',
  'IG_TARGET_1K',
  'IG_TARGET_5K_APP_REC',
]);
export type EventCreatePromotionPackageId = z.infer<typeof EventCreatePromotionPackageIdSchema>;

export const CREATE_EVENT_PROMOTION_SOCIAL_COST = 10;
export const CREATE_EVENT_PROMOTION_IG_1K_COST = 100;
export const CREATE_EVENT_PROMOTION_IG_5K_APP_REC_COST = 500;

export const EVENT_CREATE_PROMOTION_PACKAGE_COST: Record<EventCreatePromotionPackageId, number> = {
  SOCIAL_NETWORKS: CREATE_EVENT_PROMOTION_SOCIAL_COST,
  IG_TARGET_1K: CREATE_EVENT_PROMOTION_IG_1K_COST,
  IG_TARGET_5K_APP_REC: CREATE_EVENT_PROMOTION_IG_5K_APP_REC_COST,
};

/** Пакеты с таргетом в Instagram взаимоисключающие: у события одна активная ADS-задача. */
const EXCLUSIVE_ADS_PACKAGES: readonly EventCreatePromotionPackageId[] = [
  'IG_TARGET_1K',
  'IG_TARGET_5K_APP_REC',
];

export const CreateEventPromotionPackagesInput = z.object({
  packages: z
    .array(EventCreatePromotionPackageIdSchema)
    .refine((ids) => EXCLUSIVE_ADS_PACKAGES.filter((id) => ids.includes(id)).length <= 1, {
      message: 'exclusive_ads_packages',
    }),
});
export type CreateEventPromotionPackagesInput = z.infer<typeof CreateEventPromotionPackagesInput>;

export const CreateEventPromotionPackagesResponse = z.object({
  purchased: z.array(EventCreatePromotionPackageIdSchema),
});
export type CreateEventPromotionPackagesResponse = z.infer<typeof CreateEventPromotionPackagesResponse>;

/** Переключить пакет в выборе: второй IG-пакет заменяет первый, а не добавляется к нему. */
export function toggleCreatePromotionPackage(
  selected: readonly EventCreatePromotionPackageId[],
  id: EventCreatePromotionPackageId,
): EventCreatePromotionPackageId[] {
  if (selected.includes(id)) return selected.filter((p) => p !== id);
  const kept = EXCLUSIVE_ADS_PACKAGES.includes(id)
    ? selected.filter((p) => !EXCLUSIVE_ADS_PACKAGES.includes(p))
    : selected;
  return [...kept, id];
}

/** Сумма уникальных пакетов (повтор в массиве игнорируется). */
export function sumCreatePromotionPackageCost(packageIds: readonly EventCreatePromotionPackageId[]): number {
  const seen = new Set<EventCreatePromotionPackageId>();
  let total = 0;
  for (const id of packageIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    total += EVENT_CREATE_PROMOTION_PACKAGE_COST[id];
  }
  return total;
}

/** Префикс organizerNote: по нему отличаем задачи пакетов от обычного продвижения. */
export const CREATE_PACKAGE_NOTE_PREFIX = 'create_package:';

/**
 * Пакеты продаются только при создании события: их цены ниже витрины экрана «Продвижение».
 * Окно считается от создания события и покрывает отправку формы.
 */
export const CREATE_PACKAGES_WINDOW_MS = 60 * 60 * 1000;

export type CreatePackagePromotionTaskSpec = {
  channel: z.infer<typeof PromotionChannelSchema>;
  cost: number;
  organizerNote: string;
  /** Пакет 3: создать буст ленты «Я» на 100 показов. */
  homeRecommendationBoost?: { impressionsTotal: number };
};

/** Развёртывание пакета в одну или несколько задач менеджеру / внутреннему бусту. */
export function expandCreatePromotionPackage(
  id: EventCreatePromotionPackageId,
): CreatePackagePromotionTaskSpec[] {
  switch (id) {
    case 'SOCIAL_NETWORKS':
      return [
        {
          channel: 'SOCIAL',
          cost: CREATE_EVENT_PROMOTION_SOCIAL_COST,
          organizerNote: `${CREATE_PACKAGE_NOTE_PREFIX}SOCIAL_NETWORKS`,
        },
      ];
    case 'IG_TARGET_1K':
      return [
        {
          channel: 'ADS',
          cost: CREATE_EVENT_PROMOTION_IG_1K_COST,
          organizerNote: `${CREATE_PACKAGE_NOTE_PREFIX}IG_TARGET_1K;impressions=1000`,
        },
      ];
    case 'IG_TARGET_5K_APP_REC':
      return [
        {
          channel: 'ADS',
          cost: 0,
          organizerNote: `${CREATE_PACKAGE_NOTE_PREFIX}IG_TARGET_5K_APP_REC;impressions=5000`,
        },
        {
          channel: 'APP_RECOMMENDATIONS',
          cost: CREATE_EVENT_PROMOTION_IG_5K_APP_REC_COST,
          organizerNote: `${CREATE_PACKAGE_NOTE_PREFIX}IG_TARGET_5K_APP_REC;app_impressions=100`,
          homeRecommendationBoost: { impressionsTotal: 100 },
        },
      ];
  }
}
